import type { FacilityChoice, FacilityOption } from "@/contexts/AuthProvider";
import type { TranslationKeys } from "@/i18n/types";
import type { GeneralFormData } from "./types";

/** The dropdown value of the "New facility" row. Never a facility id: those are UUIDs. */
export const NEW_FACILITY_OPTION = "new";

/** The dropdown value that shows `choice` as selected; `undefined` while nothing is picked. */
export function facilityOptionOf(choice: FacilityChoice | null): string | undefined {
	if (choice === null) return undefined;
	return choice.kind === "new" ? NEW_FACILITY_OPTION : choice.facility_id;
}

/**
 * The form once a dropdown value is picked from `facilities`, the network's list (`undefined` until loaded).
 *
 * A listed facility also sets the form's time zone to the facility's: every department of a facility
 * uses one time zone, and the create is refused otherwise. It shows the facility's two inactivity windows
 * too: the new department takes the facility's (the fields are read-only then, and the server ignores them).
 * Re-picking "New facility" while it is already chosen keeps the name Ops typed.
 *
 * The EMR rows are dropped whenever the building changes: they name the previous building's connections (or the
 * ones typed for a new building), which the server refuses for another building.
 */
export function formAfterFacilityPick(form: GeneralFormData, option: string, facilities: readonly FacilityOption[] | undefined): GeneralFormData {
	if (option === NEW_FACILITY_OPTION) {
		if (form.facility?.kind === "new") return form;
		// DEFAULT: a new facility is named after its first department, which is what Ops would type for a
		// single-department building; the name field stays editable.
		return { ...form, facility: { kind: "new", name: form.displayName }, emrIntegrations: [] };
	}
	const picked = facilities?.find((facility) => facility.facility_id === option);
	if (picked === undefined) throw new Error(`Facility ${option} is not in this network's list`);
	const isSameBuilding = form.facility?.kind === "existing" && form.facility.facility_id === picked.facility_id;
	return {
		...form,
		facility: { kind: "existing", facility_id: picked.facility_id },
		emrIntegrations: isSameBuilding ? form.emrIntegrations : [],
		timezone: picked.timezone,
		nurseInactivityAlertDays: picked.nurse_inactivity_alert_days,
		nurseInactiveAfterDays: picked.nurse_inactive_after_days,
	};
}

/** A facility picked from the old network's list doesn't belong to the new one, so it's dropped. */
export function facilityChoiceAfterNetworkChange(choice: FacilityChoice | null): FacilityChoice | null {
	return choice?.kind === "existing" ? null : choice;
}

/** The one line under the picker that says what the current state means, with what it names. */
export type FacilityHint =
	| { key: "department.config.general.facility.hint.existing"; values: { facilityName: string; timezone: string } }
	| { key: Exclude<TranslationKeys, "department.config.general.facility.hint.existing"> };

/** `listed` is the network's facility list, `undefined` until it has loaded. */
export function facilityHint(network: string, listed: readonly FacilityOption[] | undefined, choice: FacilityChoice | null): FacilityHint {
	if (network === "") return { key: "department.config.general.facility.hint.networkFirst" };
	if (choice?.kind === "new") return { key: "department.config.general.facility.hint.new" };
	const picked = choice === null ? undefined : listed?.find((facility) => facility.facility_id === choice.facility_id);
	if (picked !== undefined) {
		return { key: "department.config.general.facility.hint.existing", values: { facilityName: picked.name, timezone: picked.timezone } };
	}
	if (listed?.length === 0) return { key: "department.config.general.facility.hint.empty" };
	return { key: "department.config.general.facility.hint.choose" };
}

/** Why the facility blocks Save on a new department, if it does. */
export function getFacilityChoiceReason(choice: FacilityChoice | null): TranslationKeys | undefined {
	if (choice === null) return "department.config.general.facility.required";
	if (choice.kind === "new" && !choice.name.trim()) return "department.config.general.facility.newName.required";
	return undefined;
}

/** The choice as the create endpoint takes it; throws on one Save should already have blocked. */
export function facilityChoiceForCreate(choice: FacilityChoice | null): FacilityChoice {
	if (choice === null || getFacilityChoiceReason(choice) !== undefined) {
		throw new Error("A facility must be chosen before the department is created");
	}
	return choice.kind === "new" ? { kind: "new", name: choice.name.trim() } : choice;
}
